import { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { TabView, TabPanel } from "primereact/tabview";
import { ProfileField } from "../../api/types/crm/profile-fields";
import { useUploadVideo } from "../../api/mutations/field-values/useUploadVideo";
import { useUploadVideoFromUrl } from "../../api/queries/field-values/useUploadVideoFromUrl";
import { useDeleteVideo } from "../../api/mutations/field-values/useDeleteVideo";

const DEFAULT_VIDEO_MAX_SIZE_BYTES = 50000000; // 50MB

const ALLOWED_VIDEO_DOMAINS = ["player.vimeo.com", "vimeo.com"];

interface FileSizeValidation {
  isValid: boolean;
  maxSizeInBytes: number;
  maxSizeInMB: number;
  fileSizeInMB?: string;
  errorMessage?: string;
}

const validateFileSize = (
  file: File | null,
  validationMaxSize: number | undefined,
): FileSizeValidation => {
  const maxSizeInBytes = validationMaxSize
    ? validationMaxSize * 1024
    : DEFAULT_VIDEO_MAX_SIZE_BYTES;
  const maxSizeInMB = Math.round(maxSizeInBytes / (1024 * 1024));

  if (!file) {
    return { isValid: true, maxSizeInBytes, maxSizeInMB };
  }

  const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);

  if (file.size > maxSizeInBytes) {
    return {
      isValid: false,
      maxSizeInBytes,
      maxSizeInMB,
      fileSizeInMB,
      errorMessage: `Video file is too large (${fileSizeInMB}MB). Maximum allowed size is ${maxSizeInMB}MB.`,
    };
  }

  if (file.type && !file.type.startsWith("video/")) {
    return {
      isValid: false,
      maxSizeInBytes,
      maxSizeInMB,
      fileSizeInMB,
      errorMessage: "Invalid file type. Please upload a video file.",
    };
  }

  return { isValid: true, maxSizeInBytes, maxSizeInMB, fileSizeInMB };
};

const isValidVideoEmbedUrl = (url: string | null): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return ALLOWED_VIDEO_DOMAINS.some(
      (domain) =>
        parsed.hostname === domain || parsed.hostname.endsWith("." + domain),
    );
  } catch {
    return false;
  }
};

interface TranscriptButtonProps {
  transcript: string;
  visible: boolean;
  onShow: () => void;
  onHide: () => void;
}

function TranscriptButton({
  transcript,
  visible,
  onShow,
  onHide,
}: TranscriptButtonProps) {
  return (
    <div className="mt-2">
      <Button
        type="button"
        label="Show Transcript"
        icon="pi pi-file-o"
        className="p-button-text p-button-sm"
        onClick={onShow}
      />
      <Dialog
        header="Video Transcript"
        visible={visible}
        style={{ width: "70vw", maxWidth: "800px" }}
        onHide={onHide}
        modal
        draggable={false}
        resizable={false}
      >
        <div
          className="p-4 border-1 border-round surface-50 overflow-auto"
          style={{ maxHeight: "60vh" }}
        >
          <div className="line-height-3">{transcript}</div>
        </div>
      </Dialog>
    </div>
  );
}

interface VideoFieldRendererProps {
  fieldType: ProfileField;
  fieldValueId?: string;
  value: any;
  onChange: (value: any) => void;
  readOnly?: boolean;
}

export default function VideoFieldRenderer({
  fieldType,
  fieldValueId,
  value,
  onChange,
  readOnly,
}: VideoFieldRendererProps) {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);
  const [uploading, setUploading] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoName, setVideoName] = useState("");
  const [showTranscript, setShowTranscript] = useState(false);

  // Value is the Vimeo JSON response
  const currentValue = value;

  const uploadVideo = useUploadVideo();
  const uploadVideoFromUrl = useUploadVideoFromUrl();
  const deleteVideo = useDeleteVideo();

  const onUpload = (event: FileUploadHandlerEvent) => {
    const file = event.files[0];
    const validation = validateFileSize(file, fieldType.validation?.max_size);

    if (!validation.isValid) {
      toast.current?.show({
        severity: "error",
        summary: "Upload Failed",
        detail: validation.errorMessage,
      });
      fileUploadRef.current?.clear();
      return;
    }

    setUploading(true);
    uploadVideo.mutate(
      { fieldTypeId: fieldType.id, file: event.files[0] },
      {
        onSuccess: (videoData) => {
          onChange(videoData);
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Video uploaded successfully",
          });
          setUploading(false);
        },
        onError: (error: any) => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: error.response?.data?.message || "Upload failed",
          });
          setUploading(false);
          fileUploadRef.current?.clear();
        },
      },
    );
  };

  const onUploadFromUrl = async () => {
    if (!videoUrl || videoUrl.trim() === "") {
      toast.current?.show({
        severity: "error",
        summary: "Invalid Input",
        detail: "Please enter a valid video URL",
      });
      return;
    }

    try {
      new URL(videoUrl);
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Invalid URL",
        detail: "Please enter a valid URL starting with http:// or https://",
      });
      return;
    }

    setUploading(true);
    uploadVideoFromUrl.mutate(
      {
        fieldTypeId: fieldType.id,
        videoUrl,
        videoName: videoName || "video",
      },
      {
        onSuccess: (videoData) => {
          onChange(videoData);
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Video uploaded successfully from URL",
          });
          setUploading(false);
          setVideoUrl("");
          setVideoName("");
        },
        onError: (error: any) => {
          toast.current?.show({
            severity: "error",
            summary: "Upload Failed",
            detail:
              error.response?.data?.message ||
              error.message ||
              "Upload from URL failed",
          });
          setUploading(false);
        },
      },
    );
  };

  const onRemove = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (fieldValueId) {
      deleteVideo.mutate(fieldValueId, {
        onSuccess: () => {
          onChange(null);
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Video deleted successfully",
          });
        },
        onError: () => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to delete video",
          });
        },
      });
    } else {
      onChange(null);
    }
  };

  // Helper to get player embed URL (check both snake_case and camelCase)
  const getPlayerEmbedUrl = () => {
    if (!currentValue) return null;
    return currentValue.player_embed_url || currentValue.playerEmbedUrl || null;
  };

  const playerEmbedUrl = getPlayerEmbedUrl();
  const transcript = currentValue?.transcript;

  if (readOnly) {
    if (!currentValue) return <span>No video</span>;
    return (
      <div className="flex flex-column gap-2">
        {playerEmbedUrl && isValidVideoEmbedUrl(playerEmbedUrl) ? (
          <div style={{ width: "100%", maxWidth: "640px" }}>
            <div
              style={{
                position: "relative",
                paddingBottom: "56.25%",
                height: 0,
                overflow: "hidden",
              }}
            >
              <iframe
                src={playerEmbedUrl}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Video"
              />
            </div>
            {transcript && (
              <TranscriptButton
                transcript={transcript}
                visible={showTranscript}
                onShow={() => setShowTranscript(true)}
                onHide={() => setShowTranscript(false)}
              />
            )}
          </div>
        ) : (
          <span>
            Video available at:{" "}
            <a href={currentValue.link} target="_blank" rel="noreferrer">
              {currentValue.link}
            </a>
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-3">
      <Toast ref={toast} />

      {currentValue ? (
        <div className="flex flex-column gap-2">
          <div
            style={{ position: "relative", width: "100%", maxWidth: "640px" }}
          >
            {playerEmbedUrl && isValidVideoEmbedUrl(playerEmbedUrl) ? (
              <div
                style={{
                  position: "relative",
                  paddingBottom: "56.25%",
                  height: 0,
                  overflow: "hidden",
                }}
              >
                <iframe
                  src={playerEmbedUrl}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                  }}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="Video"
                />
              </div>
            ) : (
              <div className="p-3 border-1 surface-border border-round">
                Video uploaded:{" "}
                <a href={currentValue.link} target="_blank" rel="noreferrer">
                  {currentValue.name || "Link"}
                </a>
              </div>
            )}
            <Button
              icon={
                deleteVideo.isLoading ? "pi pi-spin pi-spinner" : "pi pi-times"
              }
              className="p-button-rounded p-button-danger p-button-sm"
              style={{
                position: "absolute",
                top: "-10px",
                right: "-10px",
                zIndex: 1,
              }}
              onClick={onRemove}
              disabled={deleteVideo.isLoading}
              tooltip="Remove Video"
            />
          </div>
          {transcript && (
            <TranscriptButton
              transcript={transcript}
              visible={showTranscript}
              onShow={() => setShowTranscript(true)}
              onHide={() => setShowTranscript(false)}
            />
          )}
        </div>
      ) : (
        <TabView>
          <TabPanel header="Upload File">
            <FileUpload
              ref={fileUploadRef}
              mode="basic"
              name="file"
              accept="video/*"
              maxFileSize={
                fieldType.validation?.max_size
                  ? fieldType.validation.max_size * 1024 // Backend stores in KB
                  : DEFAULT_VIDEO_MAX_SIZE_BYTES
              }
              customUpload
              uploadHandler={onUpload}
              auto
              chooseLabel={uploading ? "Uploading..." : "Choose Video File"}
              disabled={uploading}
              onValidationFail={(eventOrFile: any) => {
                // Handle both File object (TypeScript definition) and potential event object (runtime)
                let file = eventOrFile;
                if (eventOrFile.files && eventOrFile.files.length > 0) {
                  file = eventOrFile.files[0];
                }

                const validation = validateFileSize(
                  file,
                  fieldType.validation?.max_size,
                );

                toast.current?.show({
                  severity: "error",
                  summary: "Upload Failed",
                  detail: validation.errorMessage || "File validation failed.",
                });
                setUploading(false);
                fileUploadRef.current?.clear();
              }}
            />
          </TabPanel>

          <TabPanel header="Upload from URL">
            <div className="flex flex-column gap-3">
              <div className="p-float-label">
                <InputText
                  id="video-url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  className="w-full"
                  disabled={uploading}
                  placeholder="https://example.com/video.mp4"
                />
                <label htmlFor="video-url">Video URL *</label>
              </div>

              <div className="p-float-label">
                <InputText
                  id="video-name"
                  value={videoName}
                  onChange={(e) => setVideoName(e.target.value)}
                  className="w-full"
                  disabled={uploading}
                  placeholder="My Video"
                />
                <label htmlFor="video-name">Video Name (optional)</label>
              </div>

              <Button
                label={uploading ? "Uploading..." : "Upload from URL"}
                icon={uploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
                onClick={onUploadFromUrl}
                disabled={uploading || !videoUrl}
                className="w-full"
              />
            </div>
          </TabPanel>
        </TabView>
      )}
    </div>
  );
}
